import React from 'react'

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-lg rounded-lg p-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>

          <div className="prose prose-gray max-w-none">
            <p className="text-sm text-gray-600 mb-8">
              <strong>Last updated:</strong> {new Date().toLocaleDateString()}
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-700 leading-relaxed">
                Redd Miner ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how your personal information is collected, used, and disclosed by Redd Miner when you use our Reddit lead generation and marketing automation service at reddminer.com (the "Service").
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>

              <h3 className="text-xl font-medium text-gray-800 mb-3">2.1 Information You Provide to Us</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Account Information:</strong> When you create an account, we collect your name, email address, and password.</li>
                <li><strong>Reddit API Credentials:</strong> To provide our service, you may provide Reddit application credentials (client ID, client secret, user agent) which we store securely.</li>
                <li><strong>Project Configuration:</strong> Information about your lead generation projects including keywords, target subreddits, and monitoring preferences.</li>
                <li><strong>Communication:</strong> When you contact us at support@reddminer.com, we collect the information you provide in your communications.</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3 mt-6">2.2 Information We Collect Automatically</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Usage Data:</strong> Information about how you use our Service, including features accessed, time spent, and actions taken.</li>
                <li><strong>Device Information:</strong> IP address, browser type, operating system, and device identifiers.</li>
                <li><strong>Cookies and Tracking:</strong> We use Google Analytics (tracking ID: G-Q1081DQD90) to understand Service usage and improve our offerings.</li>
                <li><strong>Reddit Data:</strong> Public Reddit posts and comments that match your monitoring criteria, which we process to identify lead opportunities.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Provide, maintain, and improve our Service</li>
                <li>Monitor Reddit for lead opportunities based on your criteria</li>
                <li>Generate AI-powered comments and responses</li>
                <li>Send you notifications about lead opportunities and Service updates</li>
                <li>Respond to your comments, questions, and customer service requests</li>
                <li>Analyze usage patterns to improve our Service</li>
                <li>Protect against fraud, abuse, and security threats</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Information Sharing and Disclosure</h2>
              <p className="text-gray-700 mb-4">We do not sell, trade, or otherwise transfer your personal information to third parties except in the following circumstances:</p>

              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Service Providers:</strong> We may share information with trusted third-party service providers who assist us in operating our Service (e.g., hosting, analytics, customer support).</li>
                <li><strong>Reddit API:</strong> We use your Reddit credentials to access Reddit's API on your behalf to monitor and post content according to your instructions.</li>
                <li><strong>AI Services:</strong> We may share content with AI service providers (like OpenAI) to generate comments and responses.</li>
                <li><strong>Legal Requirements:</strong> We may disclose information when required by law or to protect our rights, property, or safety.</li>
                <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Security</h2>
              <p className="text-gray-700 leading-relaxed">
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. This includes encryption of sensitive data, secure hosting, and regular security assessments. However, no method of transmission over the Internet or electronic storage is 100% secure.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Data Retention</h2>
              <p className="text-gray-700 leading-relaxed">
                We retain your personal information for as long as your account is active or as needed to provide you with our Service. We will retain and use your information as necessary to comply with legal obligations, resolve disputes, and enforce our agreements. You may request deletion of your account and associated data at any time.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Your Rights and Choices</h2>
              <p className="text-gray-700 mb-4">Depending on your location, you may have certain rights regarding your personal information:</p>

              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Access:</strong> Request access to your personal information</li>
                <li><strong>Correction:</strong> Request correction of inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
                <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                <li><strong>Cookies:</strong> Control cookie preferences through your browser settings</li>
              </ul>

              <p className="text-gray-700 mt-4">
                To exercise these rights, please contact us at support@reddminer.com.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. International Data Transfers</h2>
              <p className="text-gray-700 leading-relaxed">
                Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that are different from the laws of your country. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                Our Service is not intended for children under the age of 18. We do not knowingly collect personal information from children under 18. If you are a parent or guardian and believe your child has provided us with personal information, please contact us at support@reddminer.com.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Third-Party Services</h2>
              <p className="text-gray-700 leading-relaxed">
                Our Service may contain links to third-party websites or services, including Reddit, Google Analytics, and AI service providers. This Privacy Policy does not apply to the privacy practices of these third parties. We encourage you to review the privacy policies of any third-party services you use.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Changes to This Privacy Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Contact Us</h2>
              <p className="text-gray-700 leading-relaxed">
                If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mt-4">
                <p className="text-gray-700">
                  <strong>Email:</strong> support@reddminer.com<br />
                  <strong>Website:</strong> reddminer.com
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. GDPR Compliance (EU Users)</h2>
              <p className="text-gray-700 mb-4">If you are located in the European Union, you have additional rights under the General Data Protection Regulation (GDPR):</p>

              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Legal Basis:</strong> We process your data based on contract performance, legitimate interests, and consent where required.</li>
                <li><strong>Data Protection Officer:</strong> You may contact our Data Protection Officer at support@reddminer.com.</li>
                <li><strong>Supervisory Authority:</strong> You have the right to lodge a complaint with your local supervisory authority.</li>
                <li><strong>Withdraw Consent:</strong> Where processing is based on consent, you may withdraw it at any time.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. CCPA Compliance (California Users)</h2>
              <p className="text-gray-700 mb-4">If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):</p>

              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Right to Know:</strong> Request information about the personal information we collect, use, and disclose.</li>
                <li><strong>Right to Delete:</strong> Request deletion of your personal information.</li>
                <li><strong>Right to Opt-Out:</strong> Opt-out of the sale of personal information (we do not sell personal information).</li>
                <li><strong>Non-Discrimination:</strong> We will not discriminate against you for exercising your CCPA rights.</li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PrivacyPolicyPage